import React, { forwardRef, ReactNode, useContext, useMemo } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { ReactComponent as SuccessIcon } from '../../../../img/check_circle.svg';
import { ReactComponent as CloseIcon } from '../../../../img/close_new.svg';
import { ReactComponent as InformationIcon } from '../../../../img/info.svg';
import { ReactComponent as ErrorIcon } from '../../../../img/minus_circle.svg';
import { ReactComponent as WarningIcon } from '../../../../img/warning-filled.svg';
import { Theme } from '../../../../theme';
import Link from '../link/Link';
import Body02 from '../typography/Body02';
import Subtitle02 from '../typography/Subtitle02';

export enum NotificationTypeEnum {
    Information,
    Error,
    Warning,
    Success
}

const getColorsMap = (theme: Theme) => ({
    [NotificationTypeEnum.Information]: {
        foreground: theme.color.blue.royal_blue_70,
        foregroundInverse: theme.semanticColor.primary,
        background: theme.color.blue.royal_blue_10
    },
    [NotificationTypeEnum.Error]: {
        foreground: theme.semanticColor.support_01,
        foregroundInverse: theme.semanticColor.support_01,
        background: theme.color.red.red_10
    },
    [NotificationTypeEnum.Warning]: {
        foreground: theme.semanticColor.support_03,
        foregroundInverse: theme.semanticColor.support_03,
        background: theme.color.gold.gold_10
    },
    [NotificationTypeEnum.Success]: {
        foreground: theme.color.green.green_60,
        foregroundInverse: theme.color.green.green_60,
        background: theme.color.green.green_10
    }
});

export interface NotificationProps {
    action?: () => void;
    actionTitle?: string | ReactNode;
    className?: string;
    inverse?: boolean;
    noIcon?: boolean;
    onClose?: () => void;
    oneLine?: boolean;
    rounded?: boolean;
    text?: string | React.ReactNode;
    title?: string;
    type?: NotificationTypeEnum;
}

const Notification = forwardRef<HTMLDivElement, NotificationProps>((props, ref) => {

    const {
        action,
        actionTitle,
        className,
        inverse = false,
        noIcon = false,
        oneLine = false,
        onClose,
        rounded = false,
        text,
        title,
        type = NotificationTypeEnum.Information
    } = props;

    const actionPresent = action !== undefined && Boolean(actionTitle);

    const theme = useContext(ThemeContext)!;

    const handleActionClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (action) {
            action();
        }
    };

    const icon = useMemo(() => {
        if (noIcon) {
            return null;
        }

        switch (type) {
            case NotificationTypeEnum.Error:
                return <StyledErrorIcon />;
            case NotificationTypeEnum.Information:
                return <StyledInformationIcon $inverse={ inverse } />;
            case NotificationTypeEnum.Warning:
                return <StyledWarningIcon />;
            case NotificationTypeEnum.Success:
                return <StyledSuccessIcon />;
            default:
                return null;
        }
    }, [ noIcon, type, inverse ]);

    const colorsMap = useMemo(() => getColorsMap(theme), [ theme ]);

    const renderCloseCross = () => {
        if (!onClose) {
            return null;
        }

        return (
            <IconContainer>
                <StyledCloseIcon inverse={ inverse }
                    onClick={ onClose }
                />
            </IconContainer>
        );
    };

    const renderContent = () => {
        return (<Content hasAction={ !!action }
            hasIcon={ !noIcon }
            $wrap={ !oneLine }
        >
            { title
                ? (
                    <StyledHeader $wrap={ !oneLine }
                        inverse={ inverse }
                    >
                        { title }
                    </StyledHeader>
                )
                : null
            }

            { text
                ? (<StyledText $wrap={ !oneLine }
                    inverse={ inverse }
                >
                    { text }
                </StyledText>
                )
                : null
            }
        </Content>);
    };

    return (<Root className={ className }
        inverse={ inverse }
        type={ type }
        rounded={ rounded }
        ref={ ref }
    >
        { Boolean(icon) && (
            <IconContainer>
                { icon }
            </IconContainer>
        ) }

        { renderContent() }

        { actionPresent && action !== undefined &&
            <ActionLinkRoot>
                <ActionLink color={ inverse ? colorsMap[type].foregroundInverse : colorsMap[type].foreground }
                    onClick={ handleActionClick }
                >
                    { actionTitle }
                </ActionLink>
            </ActionLinkRoot>
        }

        { renderCloseCross() }
    </Root>);
});

export default Notification;

interface RootProps {
    type: NotificationTypeEnum;
    inverse: boolean;
    rounded: boolean;
}

const Root = styled.div<RootProps>`
    align-items: flex-start;
    background: ${ ({ type, inverse, theme }) => ( inverse ? theme.color.grey.grey_90 : getColorsMap(theme)[type].background ) };
    border: 1px solid ${ ({ type, inverse, theme }) => ( inverse ? theme.color.grey.grey_90 : getColorsMap(theme)[type].foreground ) };
    display: flex;
    flex-grow: 0;
    line-height: 0;
    padding: ${ ({ theme }) => `${ theme.size.size_15 } ${ theme.size.size_30 }` };
    position: relative;
    border-radius: ${ ({ theme, rounded }) => ( rounded ? theme.radius.radius_5 : '0' ) };
`;

const StyledText = styled(Body02)<{ inverse: boolean; $wrap: boolean }>`
    color: ${ ({ inverse, theme }) => (inverse ? theme.color.white : theme.color.grey.grey_90) };

    a { text-decoration: underline; }
`;

const StyledHeader = styled(Subtitle02)<{ inverse: boolean; $wrap: boolean }>`
    color: ${ ({ inverse, theme }) => (inverse ? theme.color.white : theme.color.grey.grey_90) };
`;

const IconContainer = styled.div`
    display: flex;
    flex: 0;
    width: 20px;
`;

const StyledCloseIcon = styled(CloseIcon)<{ inverse: boolean }>`
    cursor: pointer;
    display: flex;
    fill: ${ ({ inverse, theme }) => (inverse ? theme.color.white : theme.color.grey.grey_90) };
    height: 20px;
    width: 20px;

    path { fill: ${ ({ inverse, theme }) => (inverse ? theme.color.white : theme.color.grey.grey_90) }; }
`;

const Content = styled.div<{ hasAction: boolean; hasIcon: boolean; $wrap: boolean }>`
    display: flex;
    gap: ${ ({ theme }) => theme.size.size_5 };
    flex: 1;
    ${ ({ hasAction, theme }) => (!hasAction ? `padding-right: ${ theme.size.size_20 }` : '') };
    ${ ({ hasIcon, theme }) => (hasIcon ? `padding-left: ${ theme.size.size_20 }` : '') };
    
    ${ ({ $wrap }) => ($wrap ? 'flex-direction: column;' : 'flex-direction: row;') }
`;

export const ActionLinkRoot = styled.div`
    display: flex;
    flex: 0;
    max-width: 30%;
    width: fit-content;
`;

const ActionLink = styled(Link)<{ color: string }>`
    color: ${ ({ color }) => color };
    display: inline-block;
    font-size: 14px;
    line-height: ${ ({ theme }) => theme.size.size_25 };
    padding-left: ${ ({ theme }) => theme.size.size_20 };
    text-decoration: underline;
`;

const StyledErrorIcon = styled(ErrorIcon)`
    fill: ${ ({ theme }) => theme.semanticColor.support_01 };
`;

const StyledInformationIcon = styled(InformationIcon)<{ $inverse?: boolean }>`
    fill: ${ ({ $inverse, theme }) => ($inverse ? theme.color.white : theme.color.blue.royal_blue_70) };
`;

const StyledWarningIcon = styled(WarningIcon)`
    fill: ${ ({ theme }) => theme.color.gold.gold_90 }
`;
const StyledSuccessIcon = styled(SuccessIcon)`
    fill: ${ ({ theme }) => theme.color.green.green_60 }
`;
